<template>
  <v-card flat id="filterCardFilter">
    <v-divider v-if="!hideDivider" />
    <v-card-title :class="padding ? 'pb-1' : 'pa-0'">
      <v-col :cols="12" align="center" v-if="isSerachActivated" class="pa-0">
        <v-form @submit.prevent="confirmSearch">
          <v-text-field
            v-model="_search"
            :loading="isSearchLoading"
            :placeholder="$t('components.filter.label')"
            hide-details
            outlined
            dense
            clearable
            @click="$emit('clickSearch')"
            @click:clear="_search = ''"
            data-test-search-field
          >
            <template #prepend-inner>
              <filter-card-pagination-menu
                v-if="showAs === ShowAsOptionsEnum.MENU"
                :filterList="filterList"
                :large="large"
                :filter="_filter"
                :predefinedFilter="predefinedFilter"
                :search="search"
                @removeAll="selectedQuickFilters = []"
                @confirmFilter="confirmFilter"
              />
              <div
                v-else-if="showAs === ShowAsOptionsEnum.SIDE_CARD"
                class="mr-2"
                style="margin-top: -16px; margin-left: -10px"
              >
                <v-btn icon @click="toggleSideCard" data-test-filter>
                  <v-icon :color="filter.length ? 'info' : ''">
                    {{ filter.length ? "mdi-filter" : "mdi-filter-outline" }}
                  </v-icon>
                </v-btn>
                <v-divider vertical style="position: relative; top: 10px; left: 2px" />
              </div>
            </template>
            <template #append>
              <tooltip :text="`${$t('components.filter.label')}: ${filterLabel}`">
                <v-icon data-test-search-button @click="confirmSearch"> mdi-magnify </v-icon>
              </tooltip>
            </template>
          </v-text-field>
        </v-form>
      </v-col>
      <v-col cols="12" align="center" v-if="!isSerachActivated">
        <filter-card-pagination-menu
          :showAs="showAs"
          :filterList="filterList"
          :large="large"
          :filter.sync="_filter"
          :predefinedFilter="predefinedFilter"
          :search="search"
          @confirmFilter="confirmFilter"
        />
      </v-col>
      <v-col cols="12" v-if="quickFilters.length" class="pa-0">
        <div class="d-flex align-center">
          <v-chip-group v-model="selectedQuickFilters" row multiple active-class="primary--text text--accent-4">
            <v-chip
              :disabled="isSideCard"
              label
              pill
              small
              outlined
              v-for="filter in quickFilters"
              :key="'quick-filter-' + filter.name"
              :value="filter.filter"
              @click="onSelectedQuickFiltersChanged(filter.filter)"
            >
              {{ $t(filter.name) }}
            </v-chip>
          </v-chip-group>

          <v-chip-group
            v-if="activeFiltersNotPartOfQuickFilters.length"
            :value="[0]"
            active-class="primary--text text--accent-4"
          >
            <v-chip :disabled="isSideCard" color="info" label pill small outlined :value="0" @click="toggleSideCard">
              <span v-if="activeFiltersNotPartOfQuickFilters.length === 1">
                {{ $t("components.FilterCardPagination.additionalActiveFiltersSingular") }}
              </span>
              <span v-else>
                {{
                  $t("components.FilterCardPagination.additionalActiveFiltersPlural", {
                    count: activeFiltersNotPartOfQuickFilters.length
                  })
                }}
              </span>
            </v-chip>
          </v-chip-group>
        </div>
        <debug>
          <v-chip
            v-for="(filter, index) in _filter"
            :key="`filters-filter-${filter.key}-${filter.operation}-${filter.value}-${filter.type}-${index}`"
            :disabled="isSideCard"
            label
            pill
            small
            outlined
            close
            @click:close="
              () => {
                _filter.splice(index, 1);
                onConfirmFilter();
              }
            "
          >
            {{ filter.key }}
            {{ filter.operation }}
            {{ filter.value }}
            ({{ filter.type }})
          </v-chip>
        </debug>
      </v-col>
    </v-card-title>
    <v-divider v-if="!hideDivider" />
    <filter-card-pagination-side-card
      v-if="isSideCard"
      ref="sideCard"
      :filterList="filterList"
      :large="large"
      :filter.sync="_filter"
      :predefinedFilter="predefinedFilter"
      :quickFilters="quickFilters"
      :search="search"
      @confirmFilter="onConfirmFilter"
      @close="isSideCard = false"
    />
  </v-card>
</template>

<script lang="ts">
import FilterSelection from "@/components/filter/FilterSelection.vue";
import { $t } from "@/lib/utility/t";
import { IPageFilterElement } from "@/models/page-filter-element.entity";
import { PaginationFilterListElement } from "@/store/modules/base-pagination.store";
import { PredefinedFilterType } from "@/views/event/filter/event.filter";
import { Component, Prop, Vue } from "vue-property-decorator";
import CustomFieldValueChip from "../report/CustomFieldValueChip.vue";
import Debug from "../utility/Debug.vue";
import RefsUser from "../utility/RefsUser.vue";
import RefsCompany from "../utility/RefsCompany.vue";
import RefsVehicle from "../utility/RefsVehicle.vue";
import Tooltip from "../utility/tooltip.vue";
import FilterCardPaginationMenu from "./FilterCardPaginationMenu.vue";
import FilterCardPaginationSideCard from "./FilterCardPaginationSideCard.vue";
import FilterCardPaginationValueField from "./FilterCardPaginationValueField.vue";

enum ShowAsOptionsEnum {
  MENU = "menu",
  SIDE_CARD = "side-card"
}

@Component({
  components: {
    FilterSelection,
    RefsUser,
    RefsCompany,
    RefsVehicle,
    CustomFieldValueChip,
    FilterCardPaginationValueField,
    Tooltip,
    FilterCardPaginationMenu,
    FilterCardPaginationSideCard,
    Debug
  }
})
export default class FilterCardPagination extends Vue {
  readonly ShowAsOptionsEnum = ShowAsOptionsEnum;
  @Prop()
  filterList!: PaginationFilterListElement[];

  @Prop({ default: false })
  large!: boolean;

  @Prop()
  filter!: IPageFilterElement[];

  @Prop({ default: true })
  isSerachActivated!: PaginationFilterListElement[];

  @Prop({ default: false })
  hideDivider!: boolean;

  @Prop({ default: () => [] })
  predefinedFilter!: PredefinedFilterType[];

  @Prop({ default: () => [] })
  quickFilters!: PredefinedFilterType[];

  @Prop({ default: true })
  padding!: boolean;

  /**
   * Content on search bar
   */
  @Prop()
  search!: string;

  @Prop()
  isSearchLoading!: boolean;

  @Prop({ default: ShowAsOptionsEnum.MENU })
  showAs!: ShowAsOptionsEnum;

  isSideCard = false;

  selectedQuickFilters: IPageFilterElement[][] = [];

  get _filter() {
    return this.filter;
  }

  set _filter(filter: IPageFilterElement[]) {
    this.$emit("update:filter", filter);
  }

  get filterLabel() {
    const divider = "<br/>   •  ";

    if (!this.filterList) {
      return "";
    }
    const filterTexts = this.filterList.map((filter) => $t(filter.displayName ?? filter.key));

    return divider + filterTexts.join(divider);
  }

  get _search() {
    return this.search;
  }

  set _search(search: string) {
    this.$emit("update:search", search);
  }

  activeFiltersNotPartOfQuickFilters: IPageFilterElement[] = [];

  mounted() {
    this.setActiveFiltersNotPartOfQuickFilters();
    this.setSelectedQuickFiltersBasedOnSelectedFilters();
  }

  setActiveFiltersNotPartOfQuickFilters() {
    const allQuickFilterOptions = this.quickFilters.map((qf) => qf.filter).flat();

    const results = this._filter.filter(
      (f) =>
        !allQuickFilterOptions.some(
          (option) => option.key === f.key && option.value === f.value && option.operation === f.operation
        )
    );
    this.activeFiltersNotPartOfQuickFilters.splice(0);
    this.activeFiltersNotPartOfQuickFilters.push(...results);
  }

  onConfirmFilter() {
    this.setSelectedQuickFiltersBasedOnSelectedFilters();
    this.confirmFilter();
    this.setActiveFiltersNotPartOfQuickFilters();
  }

  confirmFilter() {
    this.$emit("confirmFilter");
  }

  confirmSearch() {
    this.$emit("confirmSearch");
  }

  isPredefinedFilterActive(filter: PredefinedFilterType) {
    return filter.filter.every((f) =>
      this.filter.find(
        (appliedFilter) =>
          appliedFilter.key === f.key && appliedFilter.value === f.value && appliedFilter.operation === f.operation
      )
    );
  }

  setSelectedQuickFiltersBasedOnSelectedFilters() {
    this.selectedQuickFilters.splice(0);
    for (const quickFilter of this.quickFilters) {
      if (!this.isPredefinedFilterActive(quickFilter)) {
        continue;
      }
      this.selectedQuickFilters.push(quickFilter.filter);
    }
  }

  /**
   * This is generated
   * @param toggled
   */
  onSelectedQuickFiltersChanged(toggled: IPageFilterElement[]) {
    const selectedIndex = this.selectedQuickFilters.findIndex(
      (sel) =>
        sel.length === toggled.length &&
        sel.every((s) => toggled.some((n) => n.key === s.key && n.value === s.value && n.operation === s.operation))
    );

    // If the selected Index is -1 the toggled was added, otherwise removed

    let oldSelected = [];
    let selected = [];

    if (selectedIndex === -1) {
      // Case that selected Index was added:

      oldSelected = [...this.selectedQuickFilters];
      selected = [...this.selectedQuickFilters];
      selected.push(toggled);
    } else {
      // Case that selected Index was removed:
      oldSelected = [...this.selectedQuickFilters];
      selected = [...this.selectedQuickFilters];
      selected.splice(selectedIndex, 1);
    }

    // Flatten the array of selected quick filter arrays
    const quickFilters = selected.flat();

    // Get all possible quick filters to know which ones to remove
    const allQuickFilterOptions = this.quickFilters.map((qf) => qf.filter).flat();
    const allOldQuickFilters = oldSelected.flat();

    // Filter out the quick filters that are no longer selected from the main filter array
    const otherFilters = this.filter.filter((f) => {
      const isAnOldQuickFilter = allOldQuickFilters.some(
        (oldQuick) => oldQuick.key === f.key && oldQuick.value === f.value && oldQuick.operation === f.operation
      );
      const isAQuickFilterOption = allQuickFilterOptions.some(
        (option) => option.key === f.key && option.value === f.value && option.operation === f.operation
      );

      // Keep if it's not a quick filter option, or if it is, but wasn't a previously selected one.
      // This preserves filters from other sources (like the menu).
      return !isAQuickFilterOption || !isAnOldQuickFilter;
    });

    // Add the newly selected quick filters
    const uniqueFilter: IPageFilterElement[] = [];
    for (const qf of [...quickFilters, ...otherFilters]) {
      if (!uniqueFilter.find((f) => f.key === qf.key && f.value === qf.value && f.operation === qf.operation)) {
        uniqueFilter.push(qf);
      }
    }
    this._filter = uniqueFilter;

    this.confirmFilter();
  }

  toggleSideCard() {
    if (!this.isSideCard) {
      this.isSideCard = true;
      return;
    }

    const refSideCard = this.$refs.sideCard as FilterCardPaginationSideCard;
    if (!refSideCard) {
      this.isSideCard = false;
      return;
    }

    refSideCard.onRequestClose();
  }
}
</script>
